import { Card, Tabs, Table, Button, Space, Modal, Form, Input, Select, InputNumber, Switch, Popconfirm, message, Tag, Radio, Collapse, Upload, Checkbox } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EnvironmentOutlined, StarOutlined, BankOutlined, UploadOutlined, GlobalOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api-client';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import type { Layer } from 'leaflet';
import L from 'leaflet';

// ============ INTERFACES ============

interface Region {
  id: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  code?: string;
  hasBoundary?: boolean;
  _count?: { cities: number; districts: number };
}

interface City {
  id: number;
  regionId: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  lat?: number;
  lng?: number;
  hasBoundary?: boolean;
  region?: { id: number; nameUz: string; nameRu: string };
}

interface District {
  id: number;
  regionId: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  lat?: number;
  lng?: number;
  hasBoundary?: boolean;
  region?: { id: number; nameUz: string; nameRu: string };
}

interface BoundaryMatch {
  featureIndex: number;
  featureName: string;
  featureNameEn: string;
  adminLevel: number;
  locationId: number | null;
  locationType: 'city' | 'district' | null;
  locationName: string | null;
  regionName: string | null;
  confidence: number;
  included: boolean;
}

interface BoundaryItem {
  id: number;
  nameUz: string;
  nameRu: string;
  type: 'region' | 'city' | 'district';
  regionName?: string;
  boundary: any;
}

interface PopularDestination {
  id: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  districtId: number;
  lat: number;
  lng: number;
  icon?: string;
  sortOrder: number;
  isActive: boolean;
  district?: { id: number; nameUz: string; nameRu: string; region?: { id: number; nameUz: string; nameRu: string } };
}

// Combined type for the merged cities/districts list
interface LocationItem {
  id: number;
  type: 'city' | 'district';
  regionId: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  lat?: number;
  lng?: number;
  hasBoundary?: boolean;
  region?: { id: number; nameUz: string; nameRu: string };
}

// ============ REGIONS TAB ============

const RegionsTab = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Region | null>(null);
  const [saving, setSaving] = useState(false);
  const [editBoundary, setEditBoundary] = useState<any>(null);
  const [editBoundaryLoading, setEditBoundaryLoading] = useState(false);
  const [form] = Form.useForm();

  const fetchRegions = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/admin/regions');
      setRegions(Array.isArray(response.data) ? response.data : response.data?.data || []);
    } catch {
      message.error('Viloyatlarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRegions(); }, []);

  const handleCreate = () => {
    setEditingRecord(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = async (record: Region) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setEditBoundary(null);
    setIsModalOpen(true);

    // Fetch full region data including boundary
    setEditBoundaryLoading(true);
    try {
      const res = await apiClient.get(`/admin/regions/${record.id}`);
      setEditBoundary(res.data.boundaryGeoJson || null);
    } catch {
      // Ignore - boundary just won't show
    } finally {
      setEditBoundaryLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.delete(`/admin/regions/${id}`);
      message.success('Viloyat o\'chirildi');
      fetchRegions();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleSubmit = async (values: any) => {
    setSaving(true);
    try {
      const payload = { ...values };
      if (editBoundary !== undefined) {
        payload.boundaryGeoJson = editBoundary;
      }
      if (editingRecord) {
        await apiClient.put(`/admin/regions/${editingRecord.id}`, payload);
        message.success('Viloyat yangilandi');
      } else {
        await apiClient.post('/admin/regions', payload);
        message.success('Viloyat yaratildi');
      }
      setIsModalOpen(false);
      fetchRegions();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Xatolik yuz berdi');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Nomi (UZ)', dataIndex: 'nameUz', key: 'nameUz' },
    { title: 'Nomi (RU)', dataIndex: 'nameRu', key: 'nameRu' },
    { title: 'Nomi (EN)', dataIndex: 'nameEn', key: 'nameEn' },
    { title: 'Kod', dataIndex: 'code', key: 'code', render: (code: string) => code || '-' },
    {
      title: 'Shaharlar',
      key: 'cities',
      render: (_: unknown, record: Region) => <Tag color="blue">{record._count?.cities || 0}</Tag>,
    },
    {
      title: 'Tumanlar',
      key: 'districts',
      render: (_: unknown, record: Region) => <Tag color="green">{record._count?.districts || 0}</Tag>,
    },
    {
      title: 'Chegara',
      key: 'boundary',
      width: 80,
      render: (_: unknown, record: Region) => (
        <Tag color={record.hasBoundary ? 'green' : 'default'}>{record.hasBoundary ? 'Bor' : 'Yo\'q'}</Tag>
      ),
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: Region) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="O'chirishni tasdiqlaysizmi?" onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Yangi viloyat
        </Button>
      </div>
      <Table dataSource={regions} columns={columns} rowKey="id" loading={loading} pagination={false} />
      <Modal
        title={editingRecord ? 'Viloyatni tahrirlash' : 'Yangi viloyat'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="nameUz" label="Nomi (O'zbekcha)" rules={[{ required: true, message: 'Nomni kiriting' }]}>
            <Input placeholder="Toshkent viloyati" />
          </Form.Item>
          <Form.Item name="nameRu" label="Nomi (Ruscha)" rules={[{ required: true, message: 'Nomni kiriting' }]}>
            <Input placeholder="Ташкентская область" />
          </Form.Item>
          <Form.Item name="nameEn" label="Nomi (Inglizcha)" rules={[{ required: true, message: 'Nomni kiriting' }]}>
            <Input placeholder="Tashkent Region" />
          </Form.Item>
          <Form.Item name="code" label="Kod (ixtiyoriy)">
            <Input placeholder="tashkent" />
          </Form.Item>

          {/* Boundary section */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Chegara (GeoJSON)</label>
            {editBoundaryLoading ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>Yuklanmoqda...</div>
            ) : editBoundary ? (
              <div>
                <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Tag color="green">Chegara mavjud</Tag>
                  <span style={{ fontSize: 12, color: '#999' }}>
                    Turi: {editBoundary.type || 'Polygon'}
                    {editBoundary.coordinates && `, ${editBoundary.type === 'MultiPolygon'
                      ? editBoundary.coordinates.reduce((sum: number, p: any[]) => sum + p.reduce((s: number, r: any[]) => s + r.length, 0), 0)
                      : editBoundary.coordinates.reduce((sum: number, r: any[]) => sum + r.length, 0)
                    } nuqta`}
                  </span>
                </div>
                <Input.TextArea
                  value={JSON.stringify(editBoundary, null, 2)}
                  rows={6}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setEditBoundary(parsed);
                    } catch {
                      // Allow invalid JSON while typing
                    }
                  }}
                  style={{ fontFamily: 'monospace', fontSize: 11 }}
                />
                <div style={{ marginTop: 8 }}>
                  <Popconfirm title="Chegarani o'chirishni tasdiqlaysizmi?" onConfirm={() => setEditBoundary(null)}>
                    <Button size="small" danger>Chegarani o'chirish</Button>
                  </Popconfirm>
                </div>
              </div>
            ) : (
              <div>
                <Tag color="default">Chegara yo'q</Tag>
                <div style={{ marginTop: 8 }}>
                  <Upload
                    accept=".geojson,.json"
                    showUploadList={false}
                    beforeUpload={async (file) => {
                      try {
                        const text = await file.text();
                        const parsed = JSON.parse(text);
                        if (parsed.type === 'FeatureCollection' && parsed.features?.length > 0) {
                          setEditBoundary(parsed.features[0].geometry);
                        } else if (parsed.type === 'Feature') {
                          setEditBoundary(parsed.geometry);
                        } else if (parsed.type === 'Polygon' || parsed.type === 'MultiPolygon') {
                          setEditBoundary(parsed);
                        } else {
                          message.error('GeoJSON formatini aniqlab bo\'lmadi');
                        }
                      } catch {
                        message.error('GeoJSON faylni o\'qishda xatolik');
                      }
                      return false;
                    }}
                  >
                    <Button size="small" icon={<UploadOutlined />}>GeoJSON fayl yuklash</Button>
                  </Upload>
                </div>
              </div>
            )}
          </div>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editingRecord ? 'Saqlash' : 'Yaratish'}
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>Bekor qilish</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ============ CITIES & DISTRICTS TAB (COMBINED) ============

const CitiesAndDistrictsTab = () => {
  const [items, setItems] = useState<LocationItem[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<LocationItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [itemType, setItemType] = useState<'city' | 'district'>('district');
  const [createRegionId, setCreateRegionId] = useState<number | undefined>();
  const [form] = Form.useForm();

  // GeoJSON import state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importMatches, setImportMatches] = useState<BoundaryMatch[]>([]);
  const [importGeoJson, setImportGeoJson] = useState<any>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importSaving, setImportSaving] = useState(false);
  const [allLocations, setAllLocations] = useState<LocationItem[]>([]);
  const [editBoundary, setEditBoundary] = useState<any>(null);
  const [editBoundaryLoading, setEditBoundaryLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [citiesRes, districtsRes, regionsRes] = await Promise.all([
        apiClient.get('/admin/cities'),
        apiClient.get('/admin/districts'),
        apiClient.get('/admin/regions'),
      ]);
      const cities: City[] = Array.isArray(citiesRes.data) ? citiesRes.data : citiesRes.data?.data || [];
      const districts: District[] = Array.isArray(districtsRes.data) ? districtsRes.data : districtsRes.data?.data || [];
      setRegions(Array.isArray(regionsRes.data) ? regionsRes.data : regionsRes.data?.data || []);

      const combined: LocationItem[] = [
        ...cities.map((c) => ({ ...c, type: 'city' as const })),
        ...districts.map((d) => ({ ...d, type: 'district' as const })),
      ];
      setItems(combined);
    } catch {
      message.error('Ma\'lumotlarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = (regionId?: number) => {
    setEditingRecord(null);
    setItemType('district');
    setCreateRegionId(regionId);
    form.resetFields();
    if (regionId) form.setFieldsValue({ regionId });
    setIsModalOpen(true);
  };

  const handleEdit = async (record: LocationItem) => {
    setEditingRecord(record);
    setItemType(record.type);
    setCreateRegionId(record.regionId);
    form.setFieldsValue({ ...record, regionId: record.regionId });
    setEditBoundary(null);
    setIsModalOpen(true);

    // Fetch full data including boundary
    const endpoint = record.type === 'city' ? 'cities' : 'districts';
    setEditBoundaryLoading(true);
    try {
      const res = await apiClient.get(`/admin/${endpoint}/${record.id}`);
      setEditBoundary(res.data.boundaryGeoJson || null);
    } catch {
      // Ignore - boundary just won't show
    } finally {
      setEditBoundaryLoading(false);
    }
  };

  const handleDelete = async (record: LocationItem) => {
    const endpoint = record.type === 'city' ? 'cities' : 'districts';
    try {
      await apiClient.delete(`/admin/${endpoint}/${record.id}`);
      message.success(record.type === 'city' ? 'Shahar o\'chirildi' : 'Tuman o\'chirildi');
      fetchData();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleSubmit = async (values: any) => {
    setSaving(true);
    try {
      const endpoint = itemType === 'city' ? 'cities' : 'districts';
      const payload = { ...values };
      // Include boundary data for cities and districts
      if (editBoundary !== undefined) {
        payload.boundaryGeoJson = editBoundary;
      }
      if (editingRecord) {
        await apiClient.put(`/admin/${endpoint}/${editingRecord.id}`, payload);
        message.success(itemType === 'city' ? 'Shahar yangilandi' : 'Tuman yangilandi');
      } else {
        await apiClient.post(`/admin/${endpoint}`, payload);
        message.success(itemType === 'city' ? 'Shahar yaratildi' : 'Tuman yaratildi');
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Xatolik yuz berdi');
    } finally {
      setSaving(false);
    }
  };

  // GeoJSON import handlers
  const handleOpenImport = async () => {
    setImportMatches([]);
    setImportGeoJson(null);
    setIsImportModalOpen(true);
    try {
      const [citiesRes, districtsRes] = await Promise.all([
        apiClient.get('/admin/cities'),
        apiClient.get('/admin/districts'),
      ]);
      const cities: City[] = Array.isArray(citiesRes.data) ? citiesRes.data : citiesRes.data?.data || [];
      const districts: District[] = Array.isArray(districtsRes.data) ? districtsRes.data : districtsRes.data?.data || [];
      const combined: LocationItem[] = [
        ...cities.map((c) => ({ ...c, type: 'city' as const })),
        ...districts.map((d) => ({ ...d, type: 'district' as const })),
      ];
      setAllLocations(combined);
    } catch {
      message.error('Ma\'lumotlarni yuklashda xatolik');
    }
  };

  const handleFileUpload = async (file: File) => {
    try {
      const text = await file.text();
      const geojson = JSON.parse(text);
      if (!geojson.features || !Array.isArray(geojson.features)) {
        message.error('Noto\'g\'ri GeoJSON format: features massivi topilmadi');
        return false;
      }
      setImportGeoJson(geojson);
      setImportLoading(true);
      const res = await apiClient.post('/admin/districts/import-boundaries', { geojson });
      const matches: BoundaryMatch[] = (res.data.matches || []).map((m: any) => ({
        featureIndex: m.featureIndex,
        featureName: m.featureName,
        featureNameEn: m.featureNameEn,
        adminLevel: m.adminLevel,
        locationId: m.locationId,
        locationType: m.locationType,
        locationName: m.locationName,
        regionName: m.regionName,
        confidence: m.confidence,
        included: m.confidence >= 0.5,
      }));
      setImportMatches(matches);
      message.success(`${geojson.features.length} ta element topildi`);
    } catch (err: any) {
      message.error(err.response?.data?.message || 'GeoJSON faylni o\'qishda xatolik');
    } finally {
      setImportLoading(false);
    }
    return false;
  };

  const handleSaveBoundaries = async () => {
    const toSave = importMatches
      .filter((m) => m.included && m.locationId && m.locationType)
      .map((m) => ({
        locationId: m.locationId!,
        locationType: m.locationType!,
        geojson: importGeoJson.features[m.featureIndex].geometry,
      }));
    if (toSave.length === 0) {
      message.warning('Saqlash uchun hech qanday moslik tanlanmagan');
      return;
    }
    setImportSaving(true);
    try {
      const res = await apiClient.post('/admin/districts/save-boundaries', { boundaries: toSave });
      message.success(res.data.message || `${toSave.length} ta chegara saqlandi`);
      setIsImportModalOpen(false);
      fetchData();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Saqlashda xatolik');
    } finally {
      setImportSaving(false);
    }
  };

  // Group items by region
  const groupedByRegion = regions.map((region) => {
    const regionItems = items
      .filter((item) => item.regionId === region.id)
      .sort((a, b) => {
        if (a.type !== b.type) return a.type === 'city' ? -1 : 1;
        return a.nameUz.localeCompare(b.nameUz);
      });
    const cityCount = regionItems.filter((i) => i.type === 'city').length;
    const districtCount = regionItems.filter((i) => i.type === 'district').length;
    return { region, items: regionItems, cityCount, districtCount };
  }).filter((g) => g.items.length > 0);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: 'Turi',
      key: 'type',
      width: 90,
      render: (_: unknown, record: LocationItem) => (
        <Tag color={record.type === 'city' ? 'blue' : 'green'}>
          {record.type === 'city' ? 'Shahar' : 'Tuman'}
        </Tag>
      ),
    },
    { title: 'Nomi (UZ)', dataIndex: 'nameUz', key: 'nameUz' },
    { title: 'Nomi (RU)', dataIndex: 'nameRu', key: 'nameRu' },
    {
      title: 'Koordinatalar',
      key: 'coords',
      render: (_: unknown, record: LocationItem) => (
        record.lat && record.lng
          ? <span style={{ fontSize: 12 }}>{Number(record.lat).toFixed(4)}, {Number(record.lng).toFixed(4)}</span>
          : <span style={{ color: '#999' }}>-</span>
      ),
    },
    {
      title: 'Chegara',
      key: 'boundary',
      width: 80,
      render: (_: unknown, record: LocationItem) => (
        <Tag color={record.hasBoundary ? 'green' : 'default'}>{record.hasBoundary ? 'Bor' : 'Yo\'q'}</Tag>
      ),
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: LocationItem) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="O'chirishni tasdiqlaysizmi?" onConfirm={() => handleDelete(record)}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button icon={<UploadOutlined />} onClick={handleOpenImport}>
          GeoJSON import
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleCreate()}>
          Yangi shahar/tuman
        </Button>
      </div>
      {loading ? (
        <Table dataSource={[]} columns={columns} loading={true} />
      ) : (
        <Collapse
          defaultActiveKey={groupedByRegion.map((g) => String(g.region.id))}
          items={groupedByRegion.map((group) => ({
            key: String(group.region.id),
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <strong>{group.region.nameUz}</strong>
                <Tag color="blue">{group.cityCount} shahar</Tag>
                <Tag color="green">{group.districtCount} tuman</Tag>
              </div>
            ),
            extra: (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={(e) => { e.stopPropagation(); handleCreate(group.region.id); }}
              >
                Qo'shish
              </Button>
            ),
            children: (
              <Table
                dataSource={group.items}
                columns={columns}
                rowKey={(r) => `${r.type}-${r.id}`}
                pagination={false}
                size="small"
              />
            ),
          }))}
        />
      )}
      <Modal
        title={editingRecord
          ? (editingRecord.type === 'city' ? 'Shaharni tahrirlash' : 'Tumanni tahrirlash')
          : 'Yangi shahar/tuman'
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {!editingRecord && (
            <Form.Item label="Turi">
              <Radio.Group value={itemType} onChange={(e) => setItemType(e.target.value)}>
                <Radio.Button value="district">Tuman</Radio.Button>
                <Radio.Button value="city">Shahar</Radio.Button>
              </Radio.Group>
            </Form.Item>
          )}
          <Form.Item name="regionId" label="Viloyat" rules={[{ required: true, message: 'Viloyatni tanlang' }]}>
            <Select placeholder="Viloyatni tanlang" showSearch optionFilterProp="children">
              {regions.map((r) => (
                <Select.Option key={r.id} value={r.id}>{r.nameUz}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="nameUz" label="Nomi (O'zbekcha)" rules={[{ required: true, message: 'Nomni kiriting' }]}>
            <Input placeholder={itemType === 'city' ? 'Chirchiq' : 'Yunusobod'} />
          </Form.Item>
          <Form.Item name="nameRu" label="Nomi (Ruscha)" rules={[{ required: true, message: 'Nomni kiriting' }]}>
            <Input placeholder={itemType === 'city' ? 'Чирчик' : 'Юнусабад'} />
          </Form.Item>
          <Form.Item name="nameEn" label="Nomi (Inglizcha)" rules={[{ required: true, message: 'Nomni kiriting' }]}>
            <Input placeholder={itemType === 'city' ? 'Chirchik' : 'Yunusabad'} />
          </Form.Item>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="lat" label="Kenglik (Latitude)" style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%' }} step={0.0001} placeholder="41.2995" />
            </Form.Item>
            <Form.Item name="lng" label="Uzunlik (Longitude)" style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%' }} step={0.0001} placeholder="69.2401" />
            </Form.Item>
          </div>

          {/* Boundary section for cities and districts */}
          {(
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Chegara (GeoJSON)</label>
              {editBoundaryLoading ? (
                <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>Yuklanmoqda...</div>
              ) : editBoundary ? (
                <div>
                  <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag color="green">Chegara mavjud</Tag>
                    <span style={{ fontSize: 12, color: '#999' }}>
                      Turi: {editBoundary.type || 'Polygon'}
                      {editBoundary.coordinates && `, ${editBoundary.type === 'MultiPolygon'
                        ? editBoundary.coordinates.reduce((sum: number, p: any[]) => sum + p.reduce((s: number, r: any[]) => s + r.length, 0), 0)
                        : editBoundary.coordinates.reduce((sum: number, r: any[]) => sum + r.length, 0)
                      } nuqta`}
                    </span>
                  </div>
                  <Input.TextArea
                    value={JSON.stringify(editBoundary, null, 2)}
                    rows={6}
                    onChange={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        setEditBoundary(parsed);
                      } catch {
                        // Allow invalid JSON while typing
                      }
                    }}
                    style={{ fontFamily: 'monospace', fontSize: 11 }}
                  />
                  <div style={{ marginTop: 8 }}>
                    <Popconfirm title="Chegarani o'chirishni tasdiqlaysizmi?" onConfirm={() => setEditBoundary(null)}>
                      <Button size="small" danger>Chegarani o'chirish</Button>
                    </Popconfirm>
                  </div>
                </div>
              ) : (
                <div>
                  <Tag color="default">Chegara yo'q</Tag>
                  <div style={{ marginTop: 8 }}>
                    <Upload
                      accept=".geojson,.json"
                      showUploadList={false}
                      beforeUpload={async (file) => {
                        try {
                          const text = await file.text();
                          const parsed = JSON.parse(text);
                          // Handle both raw geometry and Feature/FeatureCollection
                          if (parsed.type === 'FeatureCollection' && parsed.features?.length > 0) {
                            setEditBoundary(parsed.features[0].geometry);
                          } else if (parsed.type === 'Feature') {
                            setEditBoundary(parsed.geometry);
                          } else if (parsed.type === 'Polygon' || parsed.type === 'MultiPolygon') {
                            setEditBoundary(parsed);
                          } else {
                            message.error('GeoJSON formatini aniqlab bo\'lmadi');
                          }
                        } catch {
                          message.error('GeoJSON faylni o\'qishda xatolik');
                        }
                        return false;
                      }}
                    >
                      <Button size="small" icon={<UploadOutlined />}>GeoJSON fayl yuklash</Button>
                    </Upload>
                  </div>
                </div>
              )}
            </div>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editingRecord ? 'Saqlash' : 'Yaratish'}
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>Bekor qilish</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* GeoJSON Import Modal */}
      <Modal
        title="GeoJSON chegaralarni import qilish"
        open={isImportModalOpen}
        onCancel={() => setIsImportModalOpen(false)}
        width={900}
        footer={
          importMatches.length > 0 ? (
            <Space>
              <Button onClick={() => setIsImportModalOpen(false)}>Bekor qilish</Button>
              <Button
                type="primary"
                loading={importSaving}
                onClick={handleSaveBoundaries}
                disabled={importMatches.filter((m) => m.included && m.locationId).length === 0}
              >
                Saqlash ({importMatches.filter((m) => m.included && m.locationId).length} ta)
              </Button>
            </Space>
          ) : null
        }
      >
        {importMatches.length === 0 ? (
          <Upload.Dragger
            accept=".geojson,.json"
            showUploadList={false}
            beforeUpload={handleFileUpload}
          >
            <p style={{ fontSize: 48, color: '#999' }}>
              <UploadOutlined />
            </p>
            <p style={{ fontSize: 16 }}>GeoJSON faylni bu yerga tashlang yoki bosing</p>
            <p style={{ color: '#999' }}>.geojson yoki .json formatdagi fayl</p>
          </Upload.Dragger>
        ) : (
          <>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Jami: {importMatches.length} ta element, Tanlangan: {importMatches.filter((m) => m.included && m.locationId).length} ta</span>
              <Button size="small" onClick={() => { setImportMatches([]); setImportGeoJson(null); }}>
                Boshqa fayl yuklash
              </Button>
            </div>
            <Table
              dataSource={importMatches}
              rowKey="featureIndex"
              pagination={{ pageSize: 15 }}
              size="small"
              loading={importLoading}
              columns={[
                {
                  title: '',
                  key: 'included',
                  width: 40,
                  render: (_: unknown, record: BoundaryMatch) => (
                    <Checkbox
                      checked={record.included}
                      onChange={(e) => {
                        const updated = importMatches.map((m) =>
                          m.featureIndex === record.featureIndex ? { ...m, included: e.target.checked } : m
                        );
                        setImportMatches(updated);
                      }}
                    />
                  ),
                },
                {
                  title: 'GeoJSON nomi',
                  key: 'name',
                  render: (_: unknown, record: BoundaryMatch) => (
                    <div>
                      <div>{record.featureName}</div>
                      {record.featureNameEn && <div style={{ fontSize: 11, color: '#999' }}>{record.featureNameEn}</div>}
                    </div>
                  ),
                },
                {
                  title: 'Admin level',
                  dataIndex: 'adminLevel',
                  key: 'adminLevel',
                  width: 80,
                },
                {
                  title: 'Mos shahar/tuman',
                  key: 'location',
                  width: 280,
                  render: (_: unknown, record: BoundaryMatch) => (
                    <Select
                      style={{ width: '100%' }}
                      showSearch
                      optionFilterProp="children"
                      allowClear
                      placeholder="Shahar/tumanni tanlang"
                      value={record.locationId ? `${record.locationType}-${record.locationId}` : undefined}
                      onChange={(val) => {
                        const updated = importMatches.map((m) => {
                          if (m.featureIndex !== record.featureIndex) return m;
                          if (val) {
                            const [type, idStr] = (val as string).split('-');
                            const loc = allLocations.find((l) => l.type === type && l.id === Number(idStr));
                            return {
                              ...m,
                              locationId: Number(idStr),
                              locationType: type as 'city' | 'district',
                              locationName: loc?.nameUz || null,
                              regionName: loc?.region?.nameUz || null,
                              included: true,
                            };
                          }
                          return {
                            ...m,
                            locationId: null,
                            locationType: null,
                            locationName: null,
                            regionName: null,
                            included: false,
                          };
                        });
                        setImportMatches(updated);
                      }}
                    >
                      {allLocations.map((l) => (
                        <Select.Option key={`${l.type}-${l.id}`} value={`${l.type}-${l.id}`}>
                          {l.type === 'city' ? '🏙 ' : ''}{l.nameUz} ({l.region?.nameUz})
                        </Select.Option>
                      ))}
                    </Select>
                  ),
                },
                {
                  title: 'Moslik',
                  key: 'confidence',
                  width: 80,
                  render: (_: unknown, record: BoundaryMatch) => {
                    if (!record.locationId) return <Tag>-</Tag>;
                    const pct = Math.round(record.confidence * 100);
                    const color = pct >= 80 ? 'green' : pct >= 50 ? 'orange' : 'red';
                    return <Tag color={color}>{pct}%</Tag>;
                  },
                },
              ]}
            />
          </>
        )}
      </Modal>
    </>
  );
};

// ============ POPULAR DESTINATIONS TAB ============

const PopularDestinationsTab = () => {
  const [destinations, setDestinations] = useState<PopularDestination[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PopularDestination | null>(null);
  const [saving, setSaving] = useState(false);
  const [formRegionId, setFormRegionId] = useState<number | undefined>();
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [destRes, regionsRes] = await Promise.all([
        apiClient.get('/admin/popular-destinations'),
        apiClient.get('/admin/regions'),
      ]);
      const destData = destRes.data?.data || destRes.data || [];
      setDestinations(Array.isArray(destData) ? destData : []);
      setRegions(Array.isArray(regionsRes.data) ? regionsRes.data : regionsRes.data?.data || []);
    } catch {
      message.error('Ma\'lumotlarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
  };

  const fetchDistrictsByRegion = async (regionId: number) => {
    try {
      const res = await apiClient.get('/admin/districts', { params: { regionId } });
      setDistricts(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch {
      setDistricts([]);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = () => {
    setEditingRecord(null);
    setFormRegionId(undefined);
    setDistricts([]);
    form.resetFields();
    form.setFieldsValue({ isActive: true, sortOrder: 0 });
    setIsModalOpen(true);
  };

  const handleEdit = (record: PopularDestination) => {
    setEditingRecord(record);
    const regionId = record.district?.region?.id;
    setFormRegionId(regionId);
    if (regionId) fetchDistrictsByRegion(regionId);
    form.setFieldsValue(record);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.delete(`/admin/popular-destinations/${id}`);
      message.success('Manzil o\'chirildi');
      fetchData();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleSubmit = async (values: any) => {
    setSaving(true);
    try {
      if (editingRecord) {
        await apiClient.put(`/admin/popular-destinations/${editingRecord.id}`, values);
        message.success('Manzil yangilandi');
      } else {
        await apiClient.post('/admin/popular-destinations', values);
        message.success('Manzil yaratildi');
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Xatolik yuz berdi');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Nomi (UZ)', dataIndex: 'nameUz', key: 'nameUz' },
    { title: 'Nomi (RU)', dataIndex: 'nameRu', key: 'nameRu' },
    {
      title: 'Tuman',
      key: 'district',
      render: (_: unknown, record: PopularDestination) => record.district?.nameUz || '-',
    },
    {
      title: 'Viloyat',
      key: 'region',
      render: (_: unknown, record: PopularDestination) => record.district?.region?.nameUz || '-',
    },
    {
      title: 'Koordinatalar',
      key: 'coords',
      render: (_: unknown, record: PopularDestination) => (
        <span style={{ fontSize: 12 }}>{Number(record.lat).toFixed(4)}, {Number(record.lng).toFixed(4)}</span>
      ),
    },
    {
      title: 'Tartib',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 70,
    },
    {
      title: 'Holat',
      key: 'isActive',
      render: (_: unknown, record: PopularDestination) => (
        <Tag color={record.isActive ? 'green' : 'default'}>
          {record.isActive ? 'Faol' : 'Nofaol'}
        </Tag>
      ),
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: PopularDestination) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="O'chirishni tasdiqlaysizmi?" onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Yangi manzil
        </Button>
      </div>
      <Table dataSource={destinations} columns={columns} rowKey="id" loading={loading} pagination={false} />
      <Modal
        title={editingRecord ? 'Manzilni tahrirlash' : 'Yangi mashhur manzil'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ isActive: true, sortOrder: 0 }}>
          <Form.Item label="Viloyat" rules={[{ required: true, message: 'Viloyatni tanlang' }]}>
            <Select
              placeholder="Viloyatni tanlang"
              showSearch
              optionFilterProp="children"
              value={formRegionId}
              onChange={(val) => {
                setFormRegionId(val);
                form.setFieldValue('districtId', undefined);
                if (val) fetchDistrictsByRegion(val);
                else setDistricts([]);
              }}
            >
              {regions.map((r) => (
                <Select.Option key={r.id} value={r.id}>{r.nameUz}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="districtId" label="Tuman" rules={[{ required: true, message: 'Tumanni tanlang' }]}>
            <Select placeholder="Tumanni tanlang" showSearch optionFilterProp="children" disabled={!formRegionId}>
              {districts.map((d) => (
                <Select.Option key={d.id} value={d.id}>{d.nameUz}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="nameUz" label="Nomi (O'zbekcha)" rules={[{ required: true, message: 'Nomni kiriting' }]}>
            <Input placeholder="Toshkent aeroporti" />
          </Form.Item>
          <Form.Item name="nameRu" label="Nomi (Ruscha)" rules={[{ required: true, message: 'Nomni kiriting' }]}>
            <Input placeholder="Аэропорт Ташкент" />
          </Form.Item>
          <Form.Item name="nameEn" label="Nomi (Inglizcha)" rules={[{ required: true, message: 'Nomni kiriting' }]}>
            <Input placeholder="Tashkent Airport" />
          </Form.Item>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="lat" label="Kenglik (Latitude)" rules={[{ required: true, message: 'Kiriting' }]} style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%' }} step={0.0001} placeholder="41.2995" />
            </Form.Item>
            <Form.Item name="lng" label="Uzunlik (Longitude)" rules={[{ required: true, message: 'Kiriting' }]} style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%' }} step={0.0001} placeholder="69.2401" />
            </Form.Item>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="icon" label="Ikonka nomi (ixtiyoriy)" style={{ flex: 1 }}>
              <Input placeholder="airport, station, market..." />
            </Form.Item>
            <Form.Item name="sortOrder" label="Tartib raqami" style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </div>
          <Form.Item name="isActive" valuePropName="checked">
            <Switch checkedChildren="Faol" unCheckedChildren="Nofaol" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editingRecord ? 'Saqlash' : 'Yaratish'}
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>Bekor qilish</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ============ BOUNDARIES MAP TAB ============

const BOUNDARY_COLORS: Record<string, string> = {
  region: '#1890ff',
  city: '#52c41a',
  district: '#fa8c16',
};

// Wrap raw geometry in a GeoJSON Feature if needed
const toFeature = (geom: any): any => {
  if (!geom) return null;
  if (geom.type === 'Feature') return geom;
  if (geom.type === 'FeatureCollection') return geom;
  // Raw geometry (Polygon, MultiPolygon)
  return { type: 'Feature', properties: {}, geometry: geom };
};

const FitBoundsHelper = ({ items }: { items: BoundaryItem[] }) => {
  const map = useMap();
  useEffect(() => {
    if (items.length === 0) return;
    const group = L.featureGroup();
    items.forEach((item) => {
      try {
        const feature = toFeature(item.boundary);
        if (feature) {
          const geo = L.geoJSON(feature);
          group.addLayer(geo);
        }
      } catch { /* skip invalid */ }
    });
    if (group.getLayers().length > 0) {
      map.fitBounds(group.getBounds(), { padding: [20, 20] });
    }
  }, [items, map]);
  return null;
};

const BoundariesMapTab = () => {
  const [loading, setLoading] = useState(false);
  const [allItems, setAllItems] = useState<BoundaryItem[]>([]);
  const [showRegions, setShowRegions] = useState(true);
  const [showCities, setShowCities] = useState(true);
  const [showDistricts, setShowDistricts] = useState(true);

  const fetchBoundaries = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/boundaries');
      const data = res.data;
      const items: BoundaryItem[] = [
        ...(data.regions || []),
        ...(data.cities || []),
        ...(data.districts || []),
      ];
      setAllItems(items);
    } catch {
      message.error('Chegaralarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBoundaries(); }, []);

  const filteredItems = allItems.filter((item) => {
    if (item.type === 'region' && !showRegions) return false;
    if (item.type === 'city' && !showCities) return false;
    if (item.type === 'district' && !showDistricts) return false;
    return true;
  });

  const regionCount = allItems.filter((i) => i.type === 'region').length;
  const cityCount = allItems.filter((i) => i.type === 'city').length;
  const districtCount = allItems.filter((i) => i.type === 'district').length;

  return (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 500 }}>Filtrlar:</span>
        <Checkbox checked={showRegions} onChange={(e) => setShowRegions(e.target.checked)}>
          <Tag color="blue">Viloyatlar ({regionCount})</Tag>
        </Checkbox>
        <Checkbox checked={showCities} onChange={(e) => setShowCities(e.target.checked)}>
          <Tag color="green">Shaharlar ({cityCount})</Tag>
        </Checkbox>
        <Checkbox checked={showDistricts} onChange={(e) => setShowDistricts(e.target.checked)}>
          <Tag color="orange">Tumanlar ({districtCount})</Tag>
        </Checkbox>
        <span style={{ color: '#999', fontSize: 12 }}>
          Jami: {allItems.length} ta chegara
        </span>
        <Button size="small" onClick={fetchBoundaries} loading={loading}>Yangilash</Button>
      </div>
      {loading && allItems.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Yuklanmoqda...</div>
      ) : allItems.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
          Hech qanday chegara topilmadi. Avval viloyat, shahar yoki tumanlarga chegara qo'shing.
        </div>
      ) : (
        <div style={{ height: 600, borderRadius: 8, overflow: 'hidden', border: '1px solid #d9d9d9' }}>
          <MapContainer
            center={[41.3, 64.5]}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBoundsHelper items={filteredItems} />
            {filteredItems.map((item) => {
              const feature = toFeature(item.boundary);
              if (!feature) return null;
              const color = BOUNDARY_COLORS[item.type] || '#999';
              const label = item.type === 'region'
                ? item.nameUz
                : `${item.nameUz} (${item.regionName || ''})`;
              const typeLabel = item.type === 'region' ? 'Viloyat' : item.type === 'city' ? 'Shahar' : 'Tuman';
              return (
                <GeoJSON
                  key={`${item.type}-${item.id}-${showRegions}-${showCities}-${showDistricts}`}
                  data={feature}
                  style={{
                    color,
                    weight: item.type === 'region' ? 3 : 2,
                    opacity: 0.8,
                    fillColor: color,
                    fillOpacity: item.type === 'region' ? 0.05 : 0.15,
                    dashArray: item.type === 'region' ? '8 4' : undefined,
                  }}
                  onEachFeature={(_f: any, layer: Layer) => {
                    layer.bindTooltip(`<b>${label}</b><br/><span style="color:${color}">${typeLabel}</span>`, { sticky: true });
                  }}
                />
              );
            })}
          </MapContainer>
        </div>
      )}
    </div>
  );
};

// ============ MAIN PAGE ============

export const LocationsPage = () => {
  const tabItems = [
    {
      key: 'regions',
      label: (
        <span>
          <BankOutlined /> Viloyatlar
        </span>
      ),
      children: <RegionsTab />,
    },
    {
      key: 'cities-districts',
      label: (
        <span>
          <EnvironmentOutlined /> Shaharlar va Tumanlar
        </span>
      ),
      children: <CitiesAndDistrictsTab />,
    },
    {
      key: 'popular',
      label: (
        <span>
          <StarOutlined /> Mashhur manzillar
        </span>
      ),
      children: <PopularDestinationsTab />,
    },
    {
      key: 'map',
      label: (
        <span>
          <GlobalOutlined /> Xarita
        </span>
      ),
      children: <BoundariesMapTab />,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 24 }}>
        <EnvironmentOutlined style={{ marginRight: 8 }} />
        Joylar boshqaruvi
      </h2>
      <Card>
        <Tabs items={tabItems} defaultActiveKey="regions" />
      </Card>
    </div>
  );
};
